import React, { useEffect, useState } from 'react';
import LoggedLayout from '../../layouts/LoggedLayout';
import styled from 'styled-components';
import PageHeader from 'components/PageHeader';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import { jsonToArray } from 'helpers/generic';
import { default as PricesService } from 'services/Prices';
import CompetitionPricesFrame from './CompetitionPricesFrame';
import Details from './Details';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { getUser, setUser } from 'helpers/user';

const PricesRowsContainer = styled.div` 
	display: flex;
	flex-direction: column;
	font-size: 14px;
	line-height: 16px;

	span {
		display: block;
		width: 12px;
		height: 12px;
		border-radius: 50%;
		margin-right: 5px;

		&.goa {
			background: var(--plenoil-black);
		}

		&.goa_aditivado {
			background: var(--plenoil-orange-2);
		}

		&.sp95 {
			background: var(--plenoil-green);
		}

		&.sp95_aditivado {
			background: var(--plenoil-blue-2);
		}
	}

	div {
		display: flex;
		align-items: center;
		padding: 3px 10px;

		&:not(:first-child) {
			border-top: 1px solid #dee2e6;
		}
	}

	i {
		width: 12px;
	}

	u {
		color: var(--bs-gray-500);
		font-size: 9px;
		letter-spacing: -1px;
		margin-left: auto;
		text-decoration: none;
		padding-right: 5px;
	}
`;

const Filters = styled.div`
	display: flex;
	justify-content: end;
	align-items: center;

	.filters-inputs {
	    display: flex;
		flex-direction: row;
		align-items: center;
		flex-wrap: nowrap;

		@media (max-width: 1200px) {
			& {
				display: flex !important;
				justify-content: flex-end;
				width: 100% !important;
			}
		}

		@media (min-width: 576px) and (max-width: 1200px) {
			& > *:nth-child(2) {
				min-width: 200px;
			}
		}

		@media (min-width: 768px) and (max-width: 991px) {
			#search-box {
				flex: 1;
			}
		}

		@media (max-width: 576px) {
			flex-direction: column;
			align-items: stretch;
			gap: 1rem;
			width: 100%;
			max-width: 100%;
			box-sizing: border-box;

			& > * {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}

			#search-box {
				width: 100%;
				max-width: 100%;
				min-width: 0;
				box-sizing: border-box;
			}
		}

		@media (max-width: 374px) {
			& {
				margin: auto;
			}
		}
	}

	.filter-buttons{
		width: auto !important;

		& > * {
			@media (min-width: 1201px) {
				height: 100%;
			}

			@media (max-width: 576px) {
				width: 100%;
				text-align: center;
			}
		}

		@media (max-width: 1200px) {
			margin-right: 0.5rem;
		}

		@media (max-width: 991px) {
			width: calc(100% - 1.5rem) !important;
		}

		@media (max-width: 767px) {
			width: calc(100% - 2rem);
		}

		@media (max-width: 576px) {
			display: flex !important;
			flex-direction: column !important;
			flex-wrap: nowrap;
			align-items: stretch !important;
			width: 100% !important;
		}
	}
	
	@media (max-width: 1200px) {
		justify-content: center;
		flex-direction: column;
		align-items: flex-end;

		& > *,
		.empowered-selector {
			width: 100% !important;
			display: block !important;
		}

		& > * {
			margin-bottom: 10px;
		}
	}

	@media (max-width: 991px) {
		display: grid;
		grid-template-columns: 1fr;
		grid-auto-rows: auto;
		gap: 10px;
		align-items: stretch;

		& > * {
			width: 100% !important;
			margin-bottom: 0 !important;
		}
	}

	@media (max-width: 767px) {
		grid-template-columns: 1fr;
	}
`;

let first_competition_load = true;

export default function Prices() {
	let [stations, setStations] = useState([]);
	let [detailsOpened, setDetailsOpened] = useState([]);
	let [competitionPrices, setCompetitionPrices] = useState({});
	let [sortDirection, setSortDirection] = useState('asc');
	let [sortField, setSortField] = useState('name');
	let [onlyAlerts, setOnlyAlerts] = useState(false);
	let [zone, setZone] = useState(null);
	let [skeletonRows, setSkeletonRows] = useState(5);

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

	const openChat = (e, station_id) => {
		e.stopPropagation();

		const event = new CustomEvent('open-chat', {detail: {stationId: station_id, channel: 'prices'}});
		document.dispatchEvent(event);
	}

	const toggleDetails = (rowId) => {
		if ( competitionPrices[rowId] === undefined ) return; // Prevent toggle if data is not loaded yet

		if ( detailsOpened.includes(rowId) ) {
			detailsOpened.splice(detailsOpened.indexOf(rowId), 1);
			setDetailsOpened([...detailsOpened]);
		} else {
			setDetailsOpened([...detailsOpened, rowId]);
		}
	}

	const reloadStationCompetitionPrices = async (station_octan_id) => {
		let competitionPrices = await PricesService.getCompetitionPrices([station_octan_id]);
		setCompetitionPrices((prev) => ({...prev, ...competitionPrices}));
	}

	useEffect(() => {
		const getStations = async () => {
			let stations = await PricesService.getStations(sortField, sortDirection);
			setStations([...stations]);
			setSkeletonRows(stations.length);
		}
		getStations();

		return function cleanup() {
			PricesService.cancel();
		}
	}, [sortField, sortDirection]);

	useEffect(() => {
		const getCompetitionPrices = async () => {
			if ( stations.length <= 0 ) return; 

			let competitionPrices = await PricesService.getCompetitionPrices(stations?.map(el => el.octan_id));
			setCompetitionPrices(competitionPrices);

			if ( first_competition_load ) {
				let existsAlerts = jsonToArray(competitionPrices).filter(el => el.value.alert).length > 0;
				if ( existsAlerts ) setOnlyAlerts(true);

				first_competition_load = false;
			}
		}
		getCompetitionPrices();

		let getDataInterval = setInterval(() => {
			getCompetitionPrices();
		}, 1000 * 60); // Every 60 seconds

		return function cleanup() {
			first_competition_load = true;
			clearInterval(getDataInterval);
			PricesService.cancel();
		}
	}, [stations]);

	return (
		<LoggedLayout>
			<div className="col-md-12 mb-3">
				<PageHeader>
					<span className="header-column header-title">Control de precios</span>
					<span className="header-column" role="button">
						<div className="header-column-text">
							<i className="bi bi-exclamation-triangle" style={{color: 'var(--plenoil-red)'}}></i>
						</div>
						<div className="header-column-number ms-2" style={{color: 'var(--plenoil-red)'}}>
							{jsonToArray(competitionPrices).filter(el => el.value.alert).length}
						</div>
					</span>
					<span className="header-column header-icon-end">
						<i className="bi bi-droplet-half text-plenoil-gray"></i>
					</span>
				</PageHeader>
			</div>

			<div className="col-md-12 mb-3">
				<div className="bg-white p-2 rounded shadow-sm">
					<div className="row">
						<div className="col-lg-2 align-content-center">
							Competencia
						</div>
						<Filters className="col-lg-10 gap-1 gap-lg-2 filters-inputs">
							<EmpoweredSelector
								load={async (input, callback) => {
									let zones = await PricesService.getZonesList({no_paginate: true, search: input});
									callback(zones.map((el) => {
										return {
											value: el, 
											label: el.name
										};
									}));
								}}
								placeholder="Zonas"
								onChange={(value) => setZone(value)}
								value={zone?.id ?? null}
								label={zone?.name ?? null}
								timeout={500}
							/>
							<div className="col-md-2">
								<div className="form-check form-switch d-flex align-items-center gap-1 flex-nowrap">
									<input className="form-check-input" type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} />
									<label className="form-check-label">Sólo mostrar alertas</label>
								</div>
							</div>
						</Filters>
					</div>
				</div>
			</div>

			<div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								<div className="table-responsive-rows">
									<div className="mvc-grid mvc-grid-header-mode">
										<div className="mvc-grid-table">
											<table>
												<thead>
													<tr className="mvc-grid-headers mvc-grid-row-filters">
														<th className="title_precios-estado">Estado</th>
														<ThSortable className="title_precios-estacion" direction={sortDirection} active={sortField === 'name'} onClick={() => sortTableClick('name')}>Estación</ThSortable>
														<th className="title_precios-plenoil">Plenoil</th>
														<th className="title_precios-competencia1">Competencia 01</th>
														<th className="title_precios-competencia2">Competencia 02</th>
														<th className="title_precios-competencia3">Competencia 03</th>
														<th className="title_precios-competencia4">Competencia 04</th>
														<th className="title_precios-chat">Chat</th>
													</tr>
												</thead>
												<tbody>
													{ stations?.map((el, idx) => {
														let alert = competitionPrices ? competitionPrices[el.octan_id]?.alert ?? false : false;
														let stationCurrentPrices = competitionPrices ? competitionPrices[el.octan_id]?.current ?? [] : [];
														let stationAppPrices = competitionPrices ? competitionPrices[el.octan_id]?.app ?? [] : [];
														let stationMineturPrices = competitionPrices ? competitionPrices[el.octan_id]?.minetur ?? [] : [];

														// Reorder minetur first checked stations
														if ( stationMineturPrices && stationMineturPrices.length ) {
															// Sort by id
															stationMineturPrices.sort((a, b) => {
																if ( a.id < b.id ) return 1;
																return -1;
															});

															// Sort by checked
															stationMineturPrices.sort((a, b) => {
																if ( a.checked < b.checked ) return 1;
																return -1;
															});
														}

														// Show depending on alert
														if ( !alert && onlyAlerts ) return null;

														// Show depending if zone selected
														if ( zone && zone.stations.findIndex((zsEl) => zsEl.id === el.id) === -1 ) return null;

														// Prepare competitions first fours columns
														let competition_first_fours = [];
														for(let i = 0; i<4; i++) {
															// Get prices
															let competition = stationMineturPrices[i] ?? null;
															let appPrices = stationAppPrices[competition?.id] ?? null;
															
															// Append
															competition_first_fours.push(
																<td key={el.octan_id + '-' + i}>
																	{ competition ? 
																		<CompetitionPricesFrame 
																			station={el}
																			competition={competition} 
																			app={appPrices}
																			current={stationCurrentPrices} 
																			reloadParent={() => reloadStationCompetitionPrices(el.octan_id)}
																		/>
																		:
																		''
																	}
																</td>
															);
														}

														return (
															<React.Fragment key={'station' + el.id}>
																<tr className={' ' + (detailsOpened.includes(el.octan_id) ? 'opened' : '')} onClick={() => toggleDetails(el.octan_id)}>
																	<td className="title_precios-estado">
																		{alert === true &&
																			<span className="d-inline-flex align-items-center justify-content-center rounded-circle bg-white border shadow-sm" style={{ width: 28, height: 28 }}>
																				<span className={'status-icon d-inline-flex align-items-center justify-content-center parpadeo'} style={{width: 16, height: 16}}>
																					<i className="bi bi-exclamation-triangle" style={{color: 'var(--plenoil-red)'}}></i>
																				</span>
																			</span>
																		}
																	</td>
																	<td className="title_precios-nombre">
																		<div className="d-flex justify-content-between">
																			<div>
																				{el.name}
																			</div>
																		
																			<div className="d-flex">
																				<span className="station_code bg-dark text-white rounded-circle d-inline-flex align-items-center justify-content-center ms-1" style={{ width: '28px', height: '28px', fontSize: '11px' }}>{el.octan_id}</span>
																			</div>
																		</div>	
																	</td>
																	<td className="title_precios-precios p-0">
																		<PricesRowsContainer>
																			<div><span className="goa"></span> {stationCurrentPrices?.goa_price ?? '-'}</div>
																			<div><span className="sp95"></span> {stationCurrentPrices?.sp95_price ?? '-'}</div>
																			{stationCurrentPrices?.goa_aditivado_price > 0 &&
																				<div><span className="goa_aditivado"></span> {stationCurrentPrices?.goa_aditivado_price ? stationCurrentPrices?.goa_aditivado_price : '-'}</div>
																			}
																			{stationCurrentPrices?.sp95_aditivado_price > 0 &&
																				<div><span className="sp95_aditivado"></span> {stationCurrentPrices?.sp95_aditivado_price ? stationCurrentPrices?.sp95_aditivado_price : '-'}</div>												
																			}
																		</PricesRowsContainer>
																	</td>
																	{competition_first_fours}
																	<td className={'title_precios-chat chat ' + (!el.pending_chat_messages_count ? 'disabled' : '')}>
																		<button className="btn-plenoil-outline-blue" style={{
																			display: 'inline-flex',
																			alignItems: 'center',
																			justifyContent: 'center',
																			width: '28px',
																			height: '28px',
																			borderRadius: '50%',
																			border: '1px solid var(--plenoil-blue)'
																		}} onClick={(e) => openChat(e, el.id)}>
																			<i className={'bi bi-chat-right' + (el.pending_chat_messages_count ? '-fill' : '')}></i>
																		</button>
																	</td>
																</tr>
																{ detailsOpened.includes(el.octan_id) &&
																	<Details 
																		station={el}
																		stationMineturPrices={stationMineturPrices.filter((el, idx) => idx > 3)} 
																		stationAppPrices={stationAppPrices} 
																		stationCurrentPrices={stationCurrentPrices} 
																		reloadParent={() => reloadStationCompetitionPrices(el.octan_id)}
																	/>
																}
															</React.Fragment>
														)
													})}

													{ stations && !stations.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

													{ stations === undefined && <TrSkeleton rows={skeletonRows} columns={12} /> }
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoggedLayout>
	);
}